/// One comment in a post thread. Developer-authored comments get the
/// same emerald highlight as developer posts. Shows an Edit/Delete
/// affordance when the viewer is the author; the developer
/// additionally sees Delete on every comment (moderation). Delete is
/// behind a one-click "Sure? Yes / No" confirm.

import { useState } from 'react'
import type { DevComment, VoteValue } from '../data/devBlog'
import { Linkified } from '../util/linkify'
import { DevBlogByline } from './DevBlogByline'
import { DevBlogComposer } from './DevBlogComposer'
import { DevBlogVoteControl } from './DevBlogVoteControl'

export function DevBlogCommentRow(props: {
  comment: DevComment
  myVote: VoteValue | null
  canEdit: boolean
  canDelete: boolean
  voteDisabled?: boolean
  onVote: (next: VoteValue | null) => void
  onEdit: (body: string) => Promise<void>
  onDelete: () => Promise<void>
}) {
  const dev = props.comment.authorIsDeveloper
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  return (
    <div
      className={
        'flex gap-3 rounded-md p-2.5 ring-1 ' +
        (dev ? 'border-l-4 border-emerald-500 bg-emerald-500/5 ring-emerald-400/20' : 'bg-slate-900/40 ring-white/5')
      }
    >
      <DevBlogVoteControl
        score={props.comment.score}
        myVote={props.myVote}
        disabled={props.voteDisabled}
        onVote={props.onVote}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <DevBlogByline
            authorName={props.comment.authorName}
            authorUid={props.comment.authorUid}
            authorIsDeveloper={dev}
            createdAt={props.comment.createdAt}
            editedAt={props.comment.editedAt}
          />
          {(props.canEdit || props.canDelete) && !editing && (
            <div className="flex shrink-0 items-center gap-2 text-[11px]">
              {props.canEdit && (
                <button type="button" onClick={() => setEditing(true)} className="text-slate-400 hover:text-white">
                  Edit
                </button>
              )}
              {props.canDelete && !confirmDelete && (
                <button type="button" onClick={() => setConfirmDelete(true)} className="text-rose-400 hover:text-rose-300">
                  Delete
                </button>
              )}
              {props.canDelete && confirmDelete && (
                <>
                  <span className="text-slate-500">Sure?</span>
                  <button type="button" onClick={() => void props.onDelete()} className="text-rose-400 hover:text-rose-300">
                    Yes
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(false)} className="text-slate-400 hover:text-white">
                    No
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <div className="mt-1.5">
            <DevBlogComposer
              kind="comment"
              initialBody={props.comment.body}
              submitLabel="Save"
              autoFocus
              onCancel={() => setEditing(false)}
              onSubmit={async ({ body }) => {
                await props.onEdit(body)
                setEditing(false)
              }}
            />
          </div>
        ) : (
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-200">
            <Linkified text={props.comment.body} />
          </p>
        )}
      </div>
    </div>
  )
}
